import { create } from 'zustand';
import { useMyPrefsStore } from '@/lib/myPrefs';

/**
 * "Active session" = a device the current browser has explicitly opened
 * via the "进入操作" button on the devices grid (or via a deep-link to
 * `/devices/<serial>`).
 *
 * Phase 2.4 redesign: the sidebar used to mirror a single "current
 * device" from the URL, which made multi-device QA workflows awkward —
 * you had to bounce back to the grid every time you wanted to touch a
 * different phone. The new model lets the user keep up to N devices
 * "pinned" in the sidebar; each pin gets its own collapsible block
 * with sub-tabs (Overview / Mirror / Network / …). The browser's URL
 * still points at exactly one of them at any moment (the "focused"
 * session), but switching focus is one sidebar click — no round-trip
 * to the grid.
 *
 * 阶段 A：state 持久化迁移到 B1 类 `prefs.global.sessions.recentSerials`，
 * 跟账号走，跨电脑同步。in-memory 维持 zustand 形状以最小化调用点改动。
 * `cap` 仍由 `GET /api/config/client` 决定（A 类部署项），见 setCap。
 */

const DEFAULT_CAP = 3;

export interface AddResult {
  ok: boolean;
  reason: 'added' | 'already' | 'cap';
}

interface SessionsState {
  serials: string[];
  cap: number;
  add: (serial: string, opts?: { force?: boolean }) => AddResult;
  remove: (serial: string) => void;
  has: (serial: string) => boolean;
  setCap: (cap: number) => void;
  clear: () => void;
  replace: (serials: string[]) => void;
}

function persist(serials: string[]): void {
  useMyPrefsStore.getState().updateGlobal({ sessions: { recentSerials: serials } });
}

export const useSessions = create<SessionsState>((set, get) => ({
  // 初始 from prefs store snapshot. Empty until prefs finishes hydrating;
  // the subscribe below patches once it does.
  serials: useMyPrefsStore.getState().global.sessions.recentSerials,
  cap: DEFAULT_CAP,
  add: (serial, opts) => {
    if (!serial) return { ok: false, reason: 'cap' };
    const { serials, cap } = get();
    if (serials.includes(serial)) {
      return { ok: true, reason: 'already' };
    }
    if (serials.length >= cap) {
      if (!opts?.force) {
        return { ok: false, reason: 'cap' };
      }
      const next = [...serials.slice(1), serial];
      persist(next);
      set({ serials: next });
      return { ok: true, reason: 'added' };
    }
    const next = [...serials, serial];
    persist(next);
    set({ serials: next });
    return { ok: true, reason: 'added' };
  },
  remove: (serial) =>
    set((cur) => {
      if (!cur.serials.includes(serial)) return cur;
      const next = cur.serials.filter((s) => s !== serial);
      persist(next);
      return { serials: next };
    }),
  has: (serial) => get().serials.includes(serial),
  setCap: (cap) =>
    set((cur) => {
      if (cap <= 0 || !Number.isFinite(cap)) return cur;
      if (cur.cap === cap) return cur;
      return { cap };
    }),
  clear: () => {
    persist([]);
    set({ serials: [] });
  },
  replace: (serials) =>
    set((cur) => {
      const cleaned = serials.filter((v): v is string => typeof v === 'string' && v.length > 0);
      if (
        cleaned.length === cur.serials.length &&
        cleaned.every((s, i) => s === cur.serials[i])
      ) {
        return cur;
      }
      persist(cleaned);
      return { serials: cleaned };
    }),
}));

// One-way sync: when the prefs store hydrates (initial GET) replace the
// in-memory list with the server-backed truth. Subsequent local mutations
// flow through `persist` above; the server has no push channel for prefs
// so we only stamp once on hydrate.
if (typeof window !== 'undefined') {
  useMyPrefsStore.subscribe((state, prev) => {
    if (state.ready && !prev.ready) {
      const remote = state.global.sessions.recentSerials;
      const cur = useSessions.getState().serials;
      if (remote.length !== cur.length || remote.some((s, i) => s !== cur[i])) {
        useSessions.setState({ serials: remote });
      }
    }
  });
}
